import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import colors from '../utils/style/colors'

const NavWrap = styled.header`
  position: fixed;
  /* --pf-banner-h : hauteur du bandeau fixe (0 s'il est fermé/absent) */
  top: var(--pf-banner-h, 0px); left: 0; right: 0;
  z-index: 200;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
  transition: all 0.3s;
  background: ${(p) =>
    p.$scrolled
      ? 'rgba(10,20,8,0.96)'
      : p.$onHero
      ? 'rgba(10,20,8,0.55)'
      : 'rgba(10,20,8,0.96)'};
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${(p) =>
    p.$scrolled || !p.$onHero
      ? 'rgba(200,168,75,0.18)'
      : 'rgba(200,168,75,0.12)'};
  box-shadow: ${(p) => (p.$scrolled ? '0 4px 24px rgba(0,0,0,0.25)' : 'none')};
  @media (max-width: 768px) { padding: 0 20px; height: 58px; }
`

/* ── Logo texte bicolore ── */
const LogoText = styled(Link)`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  color: ${colors.orClair};
  span { color: rgba(255,255,255,0.55); font-weight: 400; }
  &:hover { opacity: 0.85; }
`

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: 768px) { display: none; }
`
const NavLink = styled(Link)`
  position: relative;
  color: ${(p) => (p.$active ? colors.orClair : 'rgba(255,255,255,0.8)')};
  text-decoration: none;
  font-family: ${colors.font};
  font-size: 0.86rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  padding: 8px 14px;
  border-radius: 4px;
  transition: all 0.2s;
  background: ${(p) => (p.$active ? 'rgba(200,168,75,0.1)' : 'transparent')};
  &:hover { color: ${colors.orClair}; background: rgba(200,168,75,0.08); }
  &::after {
    content: '';
    position: absolute;
    bottom: 3px; left: 14px; right: 14px;
    height: 1.5px;
    background: ${colors.or};
    transform: scaleX(${(p) => (p.$active ? 1 : 0)});
    transform-origin: center;
    transition: transform 0.3s;
    border-radius: 1px;
  }
  &:hover::after { transform: scaleX(1); }
`
const NavCta = styled(Link)`
  background: ${colors.or};
  color: ${colors.texte} !important;
  text-decoration: none;
  font-family: ${colors.font};
  font-size: 0.83rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 9px 20px;
  border-radius: 3px;
  margin-left: 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.22s;
  &:hover {
    background: ${colors.orClair};
    transform: translateY(-1px);
    box-shadow: 0 5px 18px rgba(200,168,75,0.4);
  }
`

/* ── Burger + menu mobile ── */
const Burger = styled.button`
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: rgba(255,255,255,0.85);
  font-size: 1.3rem;
  @media (max-width: 768px) { display: flex; align-items: center; }
`
const MobileMenu = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${(p) => (p.$open ? 'flex' : 'none')};
    flex-direction: column;
    position: fixed;
    top: calc(58px + var(--pf-banner-h, 0px)); left: 0; right: 0;
    background: rgba(10,20,8,0.97);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(200,168,75,0.18);
    padding: 16px 20px 28px;
    gap: 4px;
    z-index: 199;
    box-shadow: 0 12px 32px rgba(0,0,0,0.3);
  }
`
const MobileLink = styled(Link)`
  color: ${(p) => (p.$active ? colors.orClair : 'rgba(255,255,255,0.8)')};
  text-decoration: none;
  font-family: ${colors.font};
  font-size: 1rem;
  font-weight: ${(p) => (p.$active ? '700' : '500')};
  padding: 13px 16px;
  border-radius: 6px;
  background: ${(p) => (p.$active ? 'rgba(200,168,75,0.1)' : 'transparent')};
  transition: background 0.2s;
  &:hover { background: rgba(200,168,75,0.08); color: ${colors.orClair}; }
`
const MobileCta = styled(Link)`
  margin-top: 10px;
  background: ${colors.or};
  color: ${colors.texte};
  text-decoration: none;
  font-family: ${colors.font};
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 14px 20px;
  border-radius: 4px;
  text-align: center;
  transition: background 0.2s;
  &:hover { background: ${colors.orClair}; }
`


const LINKS = [
  { to: '/',          label: 'ACCUEIL'        },
  { to: '/produits',  label: 'PRODUITS'       },
  { to: '/pourquoi-nous', label: 'POURQUOI NOUS'  },
]

function Navigation() {
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const clickCount = useRef(0)
  const clickTimer = useRef(null)

  const handleLogoClick = (e) => {
    clickCount.current += 1
    clearTimeout(clickTimer.current)
    if (clickCount.current >= 5) {
      clickCount.current = 0
      window.location.href = '/admin'
    } else {
      clickTimer.current = setTimeout(() => { clickCount.current = 0 }, 500)
    }
  }

  const onHero = location.pathname === '/'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setMenuOpen(false) }, [location])

  return (
    <>
      <NavWrap $scrolled={scrolled} $onHero={onHero}>

        {/* Logo texte bicolore AchatPaille.com */}
        <LogoText to="/" onClick={handleLogoClick}>
          AchatPaille<span>.com</span>
        </LogoText>

        <NavLinks>
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} $active={location.pathname === l.to}>
              {l.label}
            </NavLink>
          ))}
          <NavCta to="/devis">
            <i className="fa-solid fa-file-invoice" />
            Devis gratuit
          </NavCta>
        </NavLinks>

        <Burger onClick={() => setMenuOpen((o) => !o)}>
          <i className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'}`} />
        </Burger>
      </NavWrap>

      <MobileMenu $open={menuOpen}>
        {LINKS.map((l) => (
          <MobileLink key={l.to} to={l.to} $active={location.pathname === l.to}>
            {l.label}
          </MobileLink>
        ))}
        <MobileCta to="/devis">Demander un devis gratuit →</MobileCta>
        <MobileLink to="/suivi-devis" $active={location.pathname === '/suivi-devis'}>
          🔍 Suivre mon devis
        </MobileLink>
      </MobileMenu>

      {/* Spacer uniquement sur les pages sans hero full-height */}
      {!onHero && <div style={{ height: 64 }} />}
    </>
  )
}

export default Navigation
